import React from "react";
import { Component } from "react";
import PropTypes from "prop-types";
import {Progress} from 'react-sweet-progress';

import LoadingOverlay from "react-loading-overlay";
import QuestionContainer from "./components/QuestionContainer";
import ButtonContainer from "./components/buttons/ButtonContainer";
import AlertDialog from "./components/AlertDialog";
import Logo from "./components/app/Logo";
import DesktopMessage from "./components/app/DesktopMessage";
import MobileMessage from "./components/app/MobileMessage";
import ChromeOnIOSErrorMessage from "./components/app/ChromeOnIOSErrorMessage";
import GeolocationMessage from "./components/app/GeolocationMessage";
import TermsOfService from "./components/app/TermsOfService";
import PrivacyPolicy from "./components/app/PrivacyPolicy"
//import Support from "./components/app/Support"
import VersionInfo from "./components/app/VersionInfo";
import MessageArea from "./components/app/MessageArea";

import strings from "./resources/strings";
import C from "./constants";
import attestivNet from "./lib/AttestivNet";
import serverApi from "./lib/serverApi";
import cookieHelper from "./helpers/cookies";

import * as deviceDetect from "react-device-detect";
import { currentQuestion } from "./state/questionStates";
import { get, has } from "lodash";
import uuidv4 from 'uuid/v4';
import CookieConsent from "react-cookie-consent";
import queryString from "query-string";

import "./App.css";
import "react-sweet-progress/lib/style.css";

const isemail = require('isemail');
const phoneNumber = require('awesome-phonenumber');

// Code block to use .env files
const dotenv = require("dotenv");
const dotenvExpand = require("dotenv-expand");
const appEnv = dotenv.config();
dotenvExpand(appEnv);

const appCfg = require("./config");

const MOBILE = "mobile", DESKTOP = "desktop", ALL_DEVICES = "all";
const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=";

var bestLocation;
var geolocationServicesUnavailable = false;
var cookieConsentAccepted = true;
var restartingWorkflow = false;

class App extends Component {
    constructor(props) {
        super(props);
        this.sendUrlLink = this.sendUrlLink.bind(this);
    }

    getChildContext() {
        return {
            store: this.props.store
        };
    }

    declineCookieConsent(self) {
        console.debug(strings.getString("declinedCookieConsent"));
        cookieConsentAccepted = false;
        self.forceUpdate();
    }

    acceptCookieConsent() {
        console.debug(strings.getString("acceptedCookieConsent"));
        cookieConsentAccepted = true;
    }

    restartWorkflowFromBeginning(state) {
        let recordId = state.recordId;
        attestivNet.updateRecord({referenceId: recordId, state: "ABANDONED"})
        .then(response => {
            if ("status" in response && response.status === "Failure") {
                console.debug(strings.getString("unableToDeleteRecord", recordId));
                console.error(response.error);
            }
        });
        restartingWorkflow = true;
        cookieHelper.removeCookie(state.referenceId);
        window.location.reload();
    }

    resetConsentCookie() {
        cookieHelper.removeCookie("AttestivCookieConsent");
        cookieConsentAccepted = true;
        window.location.reload();
    }

    createWorkflowSession(store, resolve, reject) {
        let state = store.getState();
        let firstQuestionId = state.questionSet.questionSet.defaultQuestionOrder[0];

        let deviceInfo = deviceDetect.deviceDetect();
        let allInfo = {
            deviceType: deviceDetect.deviceType,
            make: deviceInfo.vendor,
            model: deviceInfo.model,
            os: deviceInfo.os || deviceInfo.osName,
            osVersion: deviceInfo.osVersion || deviceInfo.osVersion,
            userAgent: deviceInfo.ua || deviceInfo.userAgent,
            browserName: deviceDetect.browserName,
            browserVersion: deviceDetect.fullBrowserVersion,
            browserEngineName: deviceDetect.engineName,
            browserEngineVersion: deviceDetect.engineVersion
        };
        console.info("Device and Browser Info: " + JSON.stringify(allInfo));

        attestivNet.createWorkflowSession(state, firstQuestionId, allInfo)
            .then(response => {
                console.log(response);
                if ("status" in response && response.status === "Failure") {
                    console.error(response.error);
                    reject(response.error);
                    return;
                }

                cookieHelper.setCookie(state.referenceId, response.workflowSessionId);

                store.dispatch({ type: C.INITIATE_WORKFLOW_SESSION, workflowSession: {
                    workflowSessionId: response.workflowSessionId,
                    questionSetId: state.referenceId,
                    currentQuestionId: firstQuestionId,
                    recordId: state.recordId,
                    startTime: (new Date()).getTime()
                }});

                resolve(response);
            })
            .catch(error => {
                console.error(error);
                reject(error);
            });
    }

    getWorkflowData(store, workflowDataIds) {
        for (let wdId of workflowDataIds) {
            attestivNet.getWorkflowData(wdId)
                .then(response => {
                    if ("status" in response && response.status === "Failure") {
                        console.error(response.error);
                        return;
                    }

                    store.dispatch({ type: C.LOAD_WORKFLOW_DATA, workflowData: response.workflowData, forwardAnswers: response.forwardAnswers });
                    store.dispatch({ type: C.RESUME_WORKFLOW_SESSION, showResumeMessage: true });
                })
                .catch(error => {
                    console.error(error);
                });
        }
    }

    reloadDataForWorkflowSession(store, recordId) {
        let self = this;
        let state = store.getState();

        if (state.record) {
            this.getWorkflowData(state.record.workflowData);
        }
        else if (recordId || state.recordId) {
            attestivNet.getRecord(recordId || state.recordId)
                .then(response => {
                    if ("status" in response && response.status === "Failure") {
                        console.error(response.error);
                        return;
                    }

                    store.dispatch({ type: C.SAVE_RECORD, record: response });
                    self.getWorkflowData(store, response.workflowData);
                })
                .catch(error => {
                    console.warn(strings.getString("recordNotFound", error.message));
                });
        }
    }

    parseUserDataInUrl(store) {
        // parse the reference id from the url
        let urlParts = queryString.parseUrl(window.location.href);
        if ("ud" in urlParts.query) {
            // user data being passed in. Format: ud=questionId.fieldID=value,...
            const userData = Array.isArray(urlParts.query.ud) ? urlParts.query.ud.join(",") : urlParts.query.ud;
            store.dispatch({ type: C.CAPTURE_USERDATA, data: userData })
        }
    }

    ensureWorkflowSession(store) {
        let self = this;

        return new Promise((resolve, reject) => {
            let state = store.getState();
            let workflowSessionId = cookieHelper.getCookie(state.referenceId);

            // if there is a workflowSessionId stored in localStorage, then that will override the one loaded above because
            // we are working with 2 different workflows and they are securing data with public/private keys
            let localStorageWorkflowSessionId = localStorage.getItem("attestivWORKFLOWSessionId");
            if (localStorageWorkflowSessionId) {
                store.dispatch({ type: C.UPDATE_COMPANION_WORKFLOW_SESSION_ID, companionWorkflowSessionId: localStorageWorkflowSessionId });
            }

            // if there is a cookie, then try to load the previous workflow session
            if (!workflowSessionId) {
                console.debug(strings.getString("previousWorkflowSessionIdNotFound"));
                self.createWorkflowSession(store, resolve, reject);
            }
            else {
                attestivNet.getWorkflowSession(workflowSessionId)
                    .then(response => {
                        console.log(response);
                        if ("status" in response && response.status === "Failure") {
                            console.debug(strings.getString("wsIdCreatingNewWorkflowSession"));
                            cookieHelper.removeCookie(workflowSessionId);
                            self.createWorkflowSession(store, resolve, reject);
                            return;
                        }

                        if (response.questionSetId !== state.referenceId) {
                            console.debug(strings.getString("qsCreatingNewWorkflowSession"));
                            cookieHelper.removeCookie(workflowSessionId);
                            self.createWorkflowSession(store, resolve, reject);
                        }
                        else {
                            console.debug(strings.getString("resumingWorkflowSession"));
                            let recordId = get(response, 'recordId');
                            if (recordId) {
                                store.dispatch({ type: C.CREATE_SVR_RECORD, recordId: recordId }); // add recordId to state
                            }

                            // if current question is barcodeScanner, then go back a question
                            let currentQuestionId = response.currentQuestionId;
                            let questions = state.questionSet.questionSet.questions;
                            for (let i = 0; i < questions.length; i++) {
                                let question = questions[i];
                                if (question.questionID === response.currentQuestionId
                                        && question.questionType === "barcodeScanner"
                                        && i > 0)
                                {
                                    currentQuestionId = questions[i - 1].questionID;
                                    break;
                                }    
                            }

                            store.dispatch({ type: C.INITIATE_WORKFLOW_SESSION, workflowSession: {
                                workflowSessionId: workflowSessionId,
                                questionSetId: state.referenceId,
                                currentQuestionId: currentQuestionId,
                                recordId: response.recordId
                            }});
                            self.reloadDataForWorkflowSession(store, response.recordId);
                            resolve(response);
                        }
                    })
                    .catch(error => {
                        console.error(strings.getString("wsIdCreatingNewWorkflowSession"));
                        cookieHelper.removeCookie(workflowSessionId);
                        self.createWorkflowSession(store, resolve, reject);
                    });
                return;
            }
        });
    }

    exportKey(key, keyType) {
        window.crypto.subtle.exportKey(keyType === "PUBLIC" ? "spki" : "pkcs8", key)
        .then(function(exported) {
            let exportedAsString = String.fromCharCode.apply(null, new Uint8Array(exported));
            localStorage.setItem(`attestiv${keyType}Key`, `-----BEGIN ${keyType} KEY-----\n${window.btoa(exportedAsString)}\n-----END ${keyType} KEY-----`);
            console.log(`${keyType} Key Stored:\n${localStorage.getItem(`attestiv${keyType}Key`)}`);
        })
        .catch(function(err) {
            console.error(err);
        });
    }

    generatePublicPrivateKeyPair(store) {
        let self = this;

        window.crypto.subtle.generateKey({name: "RSASSA-PKCS1-v1_5", modulusLength: 4096, publicExponent: new Uint8Array([1, 0, 1]), hash: "SHA-256"},
            true, ["sign", "verify"]
        ).then(function(keyPair) {
            console.log("Keys generated");
            self.exportKey(keyPair.publicKey, "PUBLIC");
            self.exportKey(keyPair.privateKey, "PRIVATE");
            localStorage.setItem("attestivWORKFLOWSessionId", store.getState().workflowSession.workflowSessionId); // save this off because we need it for the second workflow if we are doing something with public/private keys
        }).catch(function(err) {
            console.error(err);
        });
    }

    runWithRequestId(store, state) {
        let self = this;

        attestivNet.loggedInWithRequestId()
            .then(response => {
                if (response && "status" in response && response.status === "Success" && response.loggedIn) {
                    attestivNet.logout()
                        .then(response => {
                            console.info(strings.getString("loggedOut"));
                        })
                        .catch(error => {
                            if (error.response.data.code !== 1003) {
                                // 1003 == notLoggedIn
                                console.error(strings.getString("errorLoggingOut", error));
                            }
                        });
                }

                attestivNet.loginWithRequestId(state.requestId)
                    .then(response => {
                        if (response && "status" in response && response.status === "Failure") {
                            console.error(response.error);
                            alert(response.error + (response.detailMessage ? ": " + response.detailMessage : "" ));
                            return;
                        }
                        console.info(strings.getString("loggedIn"));
                        attestivNet.getClientRequest(state.requestId)
                            .then(clientRequest => {
                                if (clientRequest && "status" in clientRequest) {
                                    if (clientRequest.status === "Failure") {
                                        console.error(clientRequest.error);
                                        alert(clientRequest.error);
                                        return;
                                    }
                                }
                                if (
                                    clientRequest &&
                                    (!("accessCode" in state) || state.accessCode !== clientRequest.accessCode)
                                ) {
                                    alert(strings.getString("accessCodeIsInvalid"));
                                    return;
                                }
                                if (
                                    clientRequest &&
                                    "state" in clientRequest &&
                                    (clientRequest.state === "NEW" ||
                                        clientRequest.state === "STICKY" ||
                                        clientRequest.state === "STARTED" ||
                                        clientRequest.state === "INTERRUPTED" ||
                                        clientRequest.state === "ABANDONED")
                                ) {
                                    store.dispatch({ type: C.LOAD_CLIENT_REQUEST, clientRequest: clientRequest });
                                }
                                else {
                                    alert(strings.getString("invalidStateInClientRequest", (clientRequest ? clientRequest.state : "null clientRequest")));
                                    return;
                                }

                                // if client request is in NEW state, call markAsStarted() to change state to STARTED
                                if (clientRequest &&
                                    "state" in clientRequest &&
                                    clientRequest.state === 'NEW'
                                ) {
                                    attestivNet.markAsStarted(clientRequest.requestId);
                                }

                                // check if there is an addToRecord property in the client request.
                                // Be sure to use the latest state because we added the clientRequest a few lines above.
                                store.dispatch({ type: C.CREATE_SVR_RECORD, recordId: get(store.getState(), "clientRequest.addToRecord") });

                                attestivNet.getQuestionSet(state.referenceId)
                                    .then(response => {
                                        if ("status" in response && response.status === "Failure") {
                                            console.error(response.error);
                                            alert(response.error);
                                            return;
                                        }

                                        store.dispatch({ type: C.LOAD_QUESTION_SET, questionSet: response });
                                        store.dispatch({ type: C.MERGE_ADDITIONAL_QUESTIONS });
                                        store.dispatch({ type: C.INITIALIZE_QUESTION_SET, questionSet: response });

                                        if (!has(store.getState(), "clientRequest.addToRecord")) {
                                            // check if there is an addToRecord property in the question set, otherwise create an id for the record to use.
                                            store.dispatch({ type: C.CREATE_SVR_RECORD, recordId: get(store.getState(), "questionSet.addToRecord", uuidv4()) });
                                        }

                                        let addToRecord = localStorage.getItem("attestivADDToRecord");
                                        if (addToRecord) {
                                            // check if there is an addToRecord property in localStorage, if so, use that record to add to instead of creating a new one
                                            store.dispatch({ type: C.CREATE_SVR_RECORD, recordId: addToRecord });
                                        }

                                        self.parseUserDataInUrl(store);
                                        self.loadUIConfig(store);
                                        self.ensureWorkflowSession(store)
                                            .then(function() {
                                                let locationRequired = get(response, 'locationRequired', true);
                                                if (navigator.geolocation && locationRequired) {
                                                    self.captureLocation(store);
                                                }
                                                else {
                                                    console.warn(strings.getString(!locationRequired ? "locationCaptureNotRequired" : "locationServicesNotAvailable"));
                                                }

                                                if (get(store.getState(), "questionSet.generatePublicPrivateKeyPair", false)) {
                                                    self.generatePublicPrivateKeyPair(store);
                                                    // if we are generating a keypair, then there is likely a second workflow we will execute that we will
                                                    // want to add data to so save off the record id in localStorage so it can be accessed by the other workflow
                                                    localStorage.setItem("attestivADDToRecord", store.getState().recordId);
                                                }
                                            });
                                    })
                                    .catch(error => {
                                        console.error(error);
                                        alert(error && error.response && error.response.data && error.response.data.message ? error.response.data.message : error);
                                    });
                            })
                            .catch(error => {
                                console.error(error);
                                if (error.response && "data" in error.response && "message" in error.response.data) {
                                    alert(error.response.data.message);
                                }
                            });
                    })
                    .catch(error => {
                        console.error(error);
                        if (error.response && "data" in error.response && "message" in error.response.data) {
                            alert(error.response.data.message);
                        }
                    });
            })
            .catch(error => {
                console.error(error);
            });
    }

    loadUIConfig(store) {
        attestivNet.getUIConfig()
            .then(response => {
                if ("status" in response && response.status === "Failure") {
                    console.error(response.error);
                    return;
                }

                store.dispatch({ type: C.LOAD_UI_CONFIG, uiConfig: response });
                store.dispatch({ type: C.CAPTURE_ORGANIZATION, organization: response.organization });
            })
            .catch(error => {
                console.error(error);
            });
    }

    loadQuestionSetForWorkflowLogin(store, state) {
        let self = this;

        attestivNet.getQuestionSet(state.referenceId, state.organization, document.referrer)
            .then(response => {
                if ("status" in response && response.status === "Failure") {
                    console.error(response.error);
                    alert(response.error);
                    return;
                }

                if ((!response.recordNameProperties || response.recordNameProperties.length === 0) && !response.addToRecord) {
                    alert(strings.getString("needsClientRequestToRun"));
                    return;
                }

                store.dispatch({ type: C.LOAD_QUESTION_SET, questionSet: response });
                store.dispatch({ type: C.MERGE_ADDITIONAL_QUESTIONS });
                store.dispatch({ type: C.INITIALIZE_QUESTION_SET, questionSet: response });

                // check if there is an addToRecord property in the question set, otherwise create an id for the record to use.
                store.dispatch({ type: C.CREATE_SVR_RECORD, recordId: get(store.getState(), "questionSet.addToRecord", uuidv4()) });

                self.parseUserDataInUrl(store);
                self.loadUIConfig(store);
                self.ensureWorkflowSession(store)
                    .then(() => {
                        let locationRequired = get(response, 'locationRequired', true);
                        if (navigator.geolocation && locationRequired) {
                            self.captureLocation(store);
                        }
                        else {
                            console.warn(strings.getString(!locationRequired ? "locationCaptureNotRequired" : "locationServicesNotAvailable"));
                        }
                    });
            })
            .catch(error => {
                console.error(error);
                alert(error && error.response && error.response.data && error.response.data.message ? error.response.data.message : error);
            });
    }

    runWithQuestionSet(store, state) {
        let self = this;

        attestivNet.loggedInWithWorkflow()
            .then(response => {
                if (response && "status" in response && response.status === "Success" && !response.loggedIn) {
                    attestivNet.loginWithWorkflow(state.referenceId, state.organization)
                        .then(response => {
                            if (response && "status" in response && response.status === "Failure") {
                                console.error(response.error);
                                alert(response.error + (response.detailMessage ? ": " + response.detailMessage : "" ));
                                return;
                            }
                            console.info(strings.getString("loggedIn"));
                            store.dispatch({ type: C.CREATE_SVR_RECORD, recordId: get(state, uuidv4()) }); // create an id for the record to use
                            self.loadQuestionSetForWorkflowLogin(store, state);
                        })
                        .catch(error => {
                            console.error(error);
                            alert(error && error.response && error.response.data && error.response.data.message ? error.response.data.message : error);
                        });
                }
                else {
                    self.loadQuestionSetForWorkflowLogin(store, state);
                }
            });
    }

    componentWillUnmount() {
        this.unsubscribe();
    }

    async componentDidMount() {
        var store = this.props.store;
        var state = store.getState();

        this.unsubscribe = store.subscribe(() => {
            this.forceUpdate();
        });

        window.onbeforeunload = function(event) {
            let liveState = store.getState();
            // will not trigger after the clint submit.
            if(liveState.submittedWorkflow) {
                return null;
            }
            // need tbis here because we need the latest state. If we use state, then we will get the one that was set when componentDidMount was called
            // and state has likely been updated since that was called.
            if (!restartingWorkflow && has(liveState, "fileUploadsInProgress") && liveState.fileUploadsInProgress > 0) {
                console.debug("Prompt user to confirm page reload");
                return strings.getString("filesBeingUploadedInBackground");
            }
            //for the older version browser. newer has default value.
            event.returnValue = strings.getString("enteredDataWillBeLost");
        }

        serverApi.initialize(store);

        if (!("referenceId" in state) || !state.referenceId) {
            alert(strings.getString("suppliedURLInvalid"));
            return;
        }

        if (("requestId" in state && !("accessCode" in state)) || (state.requestId && !state.accessCode)) {
            alert(strings.getString("suppliedURLInvalid"));
            return;
        }

        state.requestId ? this.runWithRequestId(store, state) : this.runWithQuestionSet(store, state);
    }

    captureLocation(store, attempts = 1) {
        let self = this;
        let maxAttempts = "geolocation" in appCfg && "maxAttempts" in appCfg.geolocation ? appCfg.geolocation.maxAttempts : 30;

        navigator.geolocation.getCurrentPosition(
            function(location) {
                //console.log("Captured Position(" + attempts + "): " + location.coords.latitude + " " + location.coords.longitude + " " + location.coords.accuracy);
                if (!bestLocation || location.coords.accuracy <= bestLocation.coords.accuracy) {
                    bestLocation = location;
                    console.debug(strings.getString("positionImproved", location.coords.latitude, location.coords.longitude, location.coords.accuracy));
                    store.dispatch({ type: C.SAVE_LOCATION, location: location });
                }
                if (attempts < maxAttempts && !store.getState().submittedWorkflow) {
                    setTimeout(function() {
                        self.captureLocation(store, attempts + 1);
                    }, 3000);
                }
            },
            function(positionError) {
                switch (positionError.code) {
                    case positionError.PERMISSION_DENIED:
                        geolocationServicesUnavailable = true;
                        console.warn(strings.getString("locationPermissionDenied", positionError.message));
                        break;
                    case positionError.POSITION_UNAVAILABLE:
                        geolocationServicesUnavailable = true;
                        console.warn(strings.getString("locationPositionUnavailable", positionError.message));
                        break;
                    case positionError.TIMEOUT:
                        console.warn(strings.getString("locationTimeout", positionError.message));
                        break;
                    default:
                        geolocationServicesUnavailable = true;
                        console.warn(strings.getString("locationUnavailable", positionError.message));
                        break;
                }
                self.forceUpdate();
            },
            {
                timeout: "geolocation" in appCfg && "timeout" in appCfg.geolocation ? appCfg.geolocation.timeout : attempts === 1 ? 2000 : 20000,
                enableHighAccuracy: true,
                maximumAge: 0
            }
        );
    }

    getLocationPositionInfo(state) {
        let current = currentQuestion(state);
        if (current && (current.questionType === "submit" || current.questionType === "complete")) {
            return null;
        }

        let geolocationDebug = "geolocation" in appCfg && "debug" in appCfg.geolocation ? appCfg.geolocation.debug : false;
        let coords = state.location.coords;
        let accuracy = coords.accuracy;
        let accuracyString = strings.getString("acquiring");
        let position = null;

        if (accuracy > 128.0) {
            accuracyString = strings.getString("poor");
        }
        else if (accuracy <= 128.0 && accuracy > 64.0) {
            accuracyString = strings.getString("fair");
        }
        else if (accuracy <= 64.0 && accuracy > 32.0) {
            accuracyString = strings.getString("acceptable");
        }
        else if (accuracy <= 32.0 && accuracy > 16.0) {
            accuracyString = strings.getString("good");
        }
        else if (accuracy <= 16.0 && accuracy > 8.0) {
            accuracyString = strings.getString("great");
        }
        else if (accuracy <= 8.0) {
            accuracyString = strings.getString("outstanding");
        }

        if (!geolocationDebug) {
            position = (<div className="w3-medium">{strings.getString("locationAccuracy")}: {accuracyString}</div>);
        }
        else {
            let mapsUrl = `${MAPS_URL}${coords.latitude},${coords.longitude}`;
            position = (
                <div className="w3-medium">
                    Location Accuracy: {accuracyString}<br/>
                    Lat/Long: <a href={mapsUrl}>{coords.latitude},{coords.longitude}</a><br/>
                    Accuracy: {coords.accuracy}<br/>
                    Altitude: {coords.altitude}<br/>
                    Heading: {coords.heading}<br/>
                </div>
            );
        }

        return position;
    }

    sendUrlLink(evt) {
        let state = this.props.store.getState();

        let messageField = document.getElementById("emailSmsMessage");
        messageField.style.display = "none";
        messageField.innerHTML = "";

        let element = document.getElementById("emailSmsInputField");
        let value = element.value.trim();
        if (value && value.length > 0) {
            let number = phoneNumber(value, "US");
            let message = strings.getString("belowIsLinkRequested");
            let completionMessage = strings.getString("checkYourEmail");

            if (isemail.validate(value)) {
                console.log("Emailing link: " + window.location.href);
                attestivNet.sendMessage(value, null, message + "<br/><br/><a href=\"" + window.location.href + "\">" + strings.getString("startYourApplication") + "</a>", strings.getString("yourWebApplicationLink"),
                        state.organization ? state.organization : undefined,
                        state.clientRequest && state.clientRequest.requestId ? state.clientRequest.requestId : undefined)
                    .then((result) => {
                        document.getElementById("emailSmsLabel").innerHTML = completionMessage;
                        document.getElementById("emailSmsLabel2").innerHTML = "";
                        document.getElementById("emailSmsInputField").style.display = "none";
                        document.getElementById("emailSmsButton").style.display = "none";
                    })
                    .catch((err) => {
                        messageField.style.display = "block";
                        messageField.innerHTML = err.response.data.message;
                    });
            }
            else if (number.isValid()) {
                console.debug(strings.getString("textingLink", window.location.href));
                attestivNet.sendMessage(null, value, message + "\n\n" + window.location.href, null,
                        state.organization ? state.organization : undefined,
                        state.clientRequest && state.clientRequest.requestId ? state.clientRequest.requestId : undefined)
                    .then((result) => {
                        document.getElementById("emailSmsLabel").innerHTML = completionMessage;
                        document.getElementById("emailSmsLabel2").innerHTML = "";
                        document.getElementById("emailSmsInputField").style.display = "none";
                        document.getElementById("emailSmsButton").style.display = "none";
                    })
                    .catch((err) => {
                        messageField.style.display = "block";
                        messageField.innerHTML = err.response.data.message;
                    });
            }
            else {
                console.debug(strings.getString("invalidPhoneEmail", value));
                messageField.style.display = "block";
                messageField.innerHTML = "Invalid email/phone number";
            }
        }
    }

    getLoadingText(state) {
        if ("loadingText" in state && state.loadingText !== "") {
            return <div>{state.loadingText}</div>;
        }

        return null;
    }

    getDeviceSupport(state) {
        if ("deviceSupport" in state.questionSet) {
            switch (state.questionSet.deviceSupport.toLowerCase()) {
                case DESKTOP:
                    return DESKTOP;
                case ALL_DEVICES:
                    return ALL_DEVICES;
                case MOBILE:
                default:
                    return MOBILE;
            }
        }

        return MOBILE;
    }

    render() {
        let store = this.props.store;
        let state = store.getState();
        let self = this;

        // if a questionset could not be loaded, don't display anything
        if (!state.questionSet) {
            return (<div></div>);
        }

        if (!cookieConsentAccepted || cookieHelper.getCookie("AttestivCookieConsent") === "false") {
            return (<div className="app w3-container w3-padding-small">
                    <Logo store={store}/>
                    <div className="w3-center">
                        {strings.getString("declinedConsentMessage")}<br/>
                        <button type="button" className="w3-button w3-indigo w3-round-xxlarge w3-medium w3-padding-small w3-margin-top" onClick={() => {
                            self.resetConsentCookie();
                        }}>{strings.getString("resetCookies")}</button>
                    </div>
                </div>
            );
        }

        let isDesktopDevice = !deviceDetect.isIOS && !deviceDetect.isAndroid && !deviceDetect.isMobile && !deviceDetect.isTablet;
        let isMobileDevice = deviceDetect.isIOS || deviceDetect.isAndroid || deviceDetect.isMobile || deviceDetect.isTablet;
        //let isMobileChromeOniOS = deviceDetect.isIOS && (deviceDetect.isChrome || deviceDetect.isChromium);
        let deviceSupport = this.getDeviceSupport(state);
        let phoneModel = appCfg.debug ? deviceDetect.mobileVendor + " " + deviceDetect.mobileModel : null;
        let phoneModelDiv = phoneModel ? (<div>{phoneModel}</div>) : null;

        /*if (isMobileChromeOniOS) {
            return (<div className="app w3-container w3-padding-small">
                    <Logo store={store}/>
                    <ChromeOnIOSErrorMessage/>
                </div>
            );
        }
        else */
        if (isDesktopDevice && deviceSupport !== DESKTOP && deviceSupport !== ALL_DEVICES) {
            return (<div className="app w3-container w3-padding-small">
                    <Logo store={store}/>
                    <DesktopMessage sendUrlLinkFn={this.sendUrlLink}/>
                </div>
            );
        }
        else if (isMobileDevice && deviceSupport !== MOBILE && deviceSupport !== ALL_DEVICES) {
            return (<div className="app w3-container w3-padding-small">
                    <Logo store={store}/>
                    <MobileMessage sendUrlLinkFn={this.sendUrlLink}/>
                </div>
            );
        }
        else if (state.clientRequest || state.questionSet) {
            if ("locationRequired" in state.questionSet && state.questionSet.locationRequired && geolocationServicesUnavailable) {
                return (
                    <div className={isDesktopDevice ? "app-container" : ""}>
                        <div className="app w3-container w3-padding-small">
                            <Logo store={store}/>
                            <GeolocationMessage/>
                        </div>
                    </div>
                );
            }

            let position;
            if ("location" in state && "coords" in state.location) {
                position = this.getLocationPositionInfo(state);
            }
            let loadingText = this.getLoadingText(state);

            if (state.resumeMsgVisible) {
                setTimeout(function() {
                    store.dispatch({ type: C.HIDE_RESUME_WORKFLOW_MSG });
                }, 8000);
            }

            let currentQuestionIndex = state.workflowSession ? state.questionSet.questionSet.defaultQuestionOrder.indexOf(state.workflowSession.currentQuestionId) : 0;
            let progressPct = Math.trunc((currentQuestionIndex/(state.questionSet.questionSet.defaultQuestionOrder.length - 1)) * 100);
            let progressStyle = { width:"87%", padding: "0px 10px 0px 0px" };

            return (
                <div className={isDesktopDevice ? "app-container" : ""}>
                    <div className="app w3-container w3-padding-small">
                        <div style={{display:"flex"}}>
                            <span style={{flex:1}}>{currentQuestionIndex}/{state.questionSet.questionSet.defaultQuestionOrder.length - 1}</span>
                            <Progress style={progressStyle} percent={progressPct}/>
                        </div>
                        <Logo store={store}/>
                        <div id="resumeMessage" className={state.resumeMsgVisible ? "w3-show w3-center w3-green w3-padding-small" : "w3-hide"}>
                            {strings.getString("resumingSession")}<br/>
                            <button id="restartFromBeginning" type="button" className="w3-button w3-indigo w3-round-xxlarge w3-medium w3-padding-small w3-margin-top" onClick={() => {
                                self.restartWorkflowFromBeginning(state);
                            }}>{strings.getString("restartFromBeginning")}</button>
                        </div>
                        <MessageArea store={store}/>
                        <LoadingOverlay active={state.isLoading} spinner text={strings.getString("processing")}>
                            <QuestionContainer />
                            <ButtonContainer />
                            <AlertDialog />
                            <div className="w3-center w3-padding-16">
                                {position}
                            </div>
                        </LoadingOverlay>
                        {loadingText}
                        <div className="w3-display-bottom w3-center w3-padding-16">
                            {phoneModelDiv}
                            <PrivacyPolicy store={store}/>&nbsp;
                            <br/>
                            <TermsOfService store={store}/>&nbsp;
                            <VersionInfo store={store}/>
                        </div>
                        <CookieConsent location="bottom" buttonText={strings.getString("accept")} enableDeclineButton={true} declineButtonText={strings.getString("decline")} acceptOnScroll={false} cookieName="AttestivCookieConsent"
                                        cookieSecurity={true} expires={30} flipButtons={true} onAccept={() => this.acceptCookieConsent()} onDecline={() => this.declineCookieConsent(self)} style={{ background: "gray" }}>
                            {strings.getString("cookiesMessage")}
                        </CookieConsent>
                    </div>
                </div>
            );
        }

        return null;
    }
}

App.propTypes = {
    store: PropTypes.object.isRequired
};

App.childContextTypes = {
    store: PropTypes.object.isRequired
};

export default App;
